/**
 * Guards unsaved editor work. The app mounts a plain BrowserRouter (no data
 * router) so useBlocker is unavailable; while dirty, navigator.push/replace
 * are patched to hold the navigation for confirmation. A beforeunload
 * handler covers refresh/tab close.
 */

import { useCallback, useContext, useEffect, useRef, useState } from 'react'
// eslint-disable-next-line camelcase
import { UNSAFE_NavigationContext } from 'react-router-dom'

interface UnsavedChangesGuard {
  confirmOpen: boolean
  confirmLeave: () => void
  cancelLeave: () => void
}

export const useUnsavedChangesGuard = (when: boolean): UnsavedChangesGuard => {
  const { navigator } = useContext(UNSAFE_NavigationContext)
  const [heldNavigation, setHeldNavigation] = useState<(() => void) | null>(null)
  // Lets confirmLeave unpatch the navigator before replaying the navigation.
  const restoreRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (!when) return undefined

    const originalPush = navigator.push.bind(navigator)
    const originalReplace = navigator.replace.bind(navigator)
    const restore = () => {
      navigator.push = originalPush
      navigator.replace = originalReplace
      restoreRef.current = null
    }
    restoreRef.current = restore

    navigator.push = (...args: Parameters<typeof originalPush>) => {
      setHeldNavigation(() => () => originalPush(...args))
    }
    navigator.replace = (...args: Parameters<typeof originalReplace>) => {
      setHeldNavigation(() => () => originalReplace(...args))
    }
    return restore
  }, [when, navigator])

  useEffect(() => {
    if (!when) return undefined
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Chrome requires returnValue to be set for the native prompt.
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [when])

  const confirmLeave = useCallback(() => {
    const proceed = heldNavigation
    setHeldNavigation(null)
    restoreRef.current?.()
    proceed?.()
  }, [heldNavigation])

  const cancelLeave = useCallback(() => {
    setHeldNavigation(null)
  }, [])

  return { confirmOpen: heldNavigation !== null, confirmLeave, cancelLeave }
}

export default useUnsavedChangesGuard
